import { Questionnaire } from '../types';

export const healthQuestionnaire: Questionnaire = {
  sections: [
    {
      id: 'pregnancy',
      title: 'Pregnancy',
      questions: [
        {
          id: 'q32',
          type: 'multi-select',
          question: 'Have you ever been pregnant?',
          options: [
            {
              label: 'I am currently pregnant',
              value: 'I am currently pregnant',
            },
            {
              label: 'I have been pregnant and have children',
              value: 'I have been pregnant and have children',
            },
            {
              label: 'I have been pregnant but do not have children',
              value: 'I have been pregnant but do not have children',
            },
            {
              label: 'I have never been pregnant',
              value: 'I have never been pregnant',
            },
          ],
        },
        {
          id: 'q33',
          type: 'single-select',
          question: 'How many children do you have?',
          options: [
            { label: '1', value: '1' },
            { label: '2', value: '2' },
            { label: '3', value: '3' },
            { label: '4', value: '4' },
            { label: '5', value: '5' },
            { label: '6', value: '6' },
            { label: 'More than 6', value: 'More than 6' },
          ],
          showIf: { q32: ['I have been pregnant and have children'] },
        },
        {
          id: 'q34',
          type: 'multi-select',
          question:
            'Have you experienced any pregnancy-related losses or complications?',
          options: [
            {
              label: 'First-trimester miscarriage',
              value: 'First-trimester miscarriage',
            },
            {
              label: 'Second-trimester miscarriage',
              value: 'Second-trimester miscarriage',
            },
            { label: 'Stillbirth', value: 'Stillbirth' },
            { label: 'Gestational diabetes', value: 'Gestational diabetes' },
            { label: 'No', value: 'No' },
            { label: 'Other', value: 'Other', additionalInfo: true },
          ],
          showIf: {
            q32: [
              'I have been pregnant and have children',
              'I have been pregnant but do not have children',
            ],
          },
        },
        {
          id: 'q35',
          type: 'multi-select',
          question:
            'Have you ever undergone any of the following assisted reproductive technologies (ART)?',
          options: [
            { label: 'Egg freezing', value: 'Egg freezing' },
            {
              label: 'IVF (in vitro fertilization)',
              value: 'IVF (in vitro fertilization)',
            },
            {
              label: 'IUI (intrauterine insemination)',
              value: 'IUI (intrauterine insemination)',
            },
            {
              label: "No, I haven't undergone any ART procedures",
              value: "No, I haven't undergone any ART procedures",
            },
            { label: 'Other', value: 'Other', additionalInfo: true },
          ],
        },
        {
          id: 'q36',
          type: 'number',
          question: 'How many ART cycles have you undergone?',
          min: 0,
          max: 20,
          showIf: {
            q35: { not: ["No, I haven't undergone any ART procedures"] },
          },
          showSlider: true,
        },
        {
          id: 'q37',
          type: 'single-select',
          question:
            'Have you gone through any surgeries to reproductive organs?',
          options: [
            { label: 'Yes', value: 'Yes', additionalInfo: true },
            { label: 'No', value: 'No' },
          ],
        },
      ],
    },
    {
      id: 'health_conditions',
      title: 'Health Conditions',
      questions: [
        {
          id: 'q38',
          type: 'multi-select',
          question: 'What medications are you currently taking?',
          options: [
            {
              label: 'Hormone replacement therapy (HRT)',
              value: 'Hormone replacement therapy (HRT)',
            },
            {
              label: 'Blood glucose-lowering medications',
              value: 'Blood glucose-lowering medications',
            },
            { label: 'Thyroid medications', value: 'Thyroid medications' },
            {
              label: 'Blood pressure medications',
              value: 'Blood pressure medications',
            },
            {
              label: 'Cholesterol lowering medications',
              value: 'Cholesterol lowering medications',
            },
            { label: 'Blood thinners', value: 'Blood thinners' },
            { label: 'Fertility medications', value: 'Fertility medications' },
            { label: 'Antidepressants', value: 'Antidepressants' },
            {
              label: 'Pain relief medications',
              value: 'Pain relief medications',
            },
            {
              label: 'None of the above',
              value: null,
            },
          ],
        },
        {
          id: 'q39',
          type: 'single-select',
          question: 'Do you have any known allergies to medications?',
          options: [
            { label: 'Yes', value: 'Yes', additionalInfo: true },
            { label: 'No', value: 'No' },
          ],
        },
        {
          id: 'q40',
          type: 'multi-select',
          question: 'Which of the following supplements do you currently take?',
          options: [
            { label: 'Magnesium glycinate', value: 'Magnesium glycinate' },
            { label: 'Magnesium threonate', value: 'Magnesium threonate' },
            { label: 'Magnesium citrate', value: 'Magnesium citrate' },
            { label: 'Omega 3', value: 'Omega 3' },
            { label: 'Berberine', value: 'Berberine' },
            { label: 'Vitamin D', value: 'Vitamin D' },
            { label: 'Vitamin B12', value: 'Vitamin B12' },
            { label: 'Inositol', value: 'Inositol' },
            { label: 'Ashwagandha', value: 'Ashwagandha' },
            { label: 'Multivitamin', value: 'Multivitamin' },
            { label: 'Folic acid', value: 'Folic acid' },
            { label: 'None of the above', value: null },
          ],
        },
      ],
    },
    {
      id: 'nutrition_assessment',
      title: 'Nutrition Assessment',
      questions: [
        {
          id: 'q41',
          type: 'multi-select',
          question: 'Do you have any food intolerances?',
          questionSlug: 'food_intolerances',
          options: [
            {
              label: 'Gluten',
              value: 'Gluten',
              answerSlug: 'gluten_intolerance',
            },
            {
              label: 'Nuts',
              value: 'Nuts',
              answerSlug: 'nut_intolerance',
            },
            {
              label: 'Meat or poultry',
              value: 'Meat or poultry',
              answerSlug: 'meat_intolerance',
            },
            {
              label: 'Fish',
              value: 'Fish',
              answerSlug: 'fish_intolerance',
            },
            {
              label: 'Eggs',
              value: 'Eggs',
              answerSlug: 'egg_intolerance',
            },
            {
              label: 'Dairy',
              value: 'Dairy',
              answerSlug: 'dairy_intolerance',
            },
            {
              label: 'Soy',
              value: 'Soy',
              answerSlug: 'soy_intolerance',
            },
            {
              label: 'Other',
              value: 'Other',
              additionalInfo: true,
              answerSlug: 'other_intolerance',
            },
          ],
        },
        {
          id: 'q42',
          type: 'multi-select',
          question: 'Please indicate your regular food intake habits.',
          options: [
            { label: 'Vegetables', value: 'Vegetables' },
            { label: 'Fruit', value: 'Fruit' },
            { label: 'Lentils or legumes', value: 'Lentils or legumes' },
            { label: 'Dairy', value: 'Dairy' },
            { label: 'Meat or poultry', value: 'Meat or poultry' },
            { label: 'Fatty fish', value: 'Fatty fish' },
            { label: 'Whole grains', value: 'Whole grains' },
            { label: 'Refined grains', value: 'Refined grains' },
            { label: 'Olive oil', value: 'Olive oil' },
            {
              label: 'Ultra-processed foods and sugar',
              value: 'Ultra-processed foods and sugar',
            },
          ],
        },
        {
          id: 'q42-detail',
          type: 'group-detail',
          question: 'How often do you eat these foods?',
          dependsOn: 'q42',
          options: [
            { label: 'Daily', value: 'Daily' },
            { label: 'A few times a week', value: 'A few times a week' },
            { label: 'Rarely', value: 'Rarely' },
          ],
        },
      ],
    },
    {
      id: 'movement_sleep',
      title: 'Movement & Sleep',
      questions: [
        {
          id: 'q43',
          type: 'single-select',
          question:
            'How many times a week do you perform moderate intensity activity?',
          helperText: 'For a stretch of more than or equal to 30 minutes',
          questionSlug: 'exercise_frequency',
          options: [
            {
              label: '1-2 times per week',
              value: '1-2 times per week',
              answerSlug: 'low_exercise',
            },
            {
              label: '3-5 times per week',
              value: '3-5 times per week',
              answerSlug: 'moderate_exercise',
            },
            {
              label: 'More than 6 times per week',
              value: 'More than 6 times per week',
              answerSlug: 'high_exercise',
            },
            {
              label: "I don't do moderate-intensity activity",
              value: "I don't do moderate-intensity activity",
              answerSlug: 'sedentary',
            },
          ],
        },
        {
          id: 'q44',
          type: 'single-select',
          question: 'How many hours of sleep do you usually get at night?',
          questionSlug: 'sleep_duration',
          options: [
            {
              label: 'Less than 6 hours',
              value: 'Less than 6 hours',
              answerSlug: 'insufficient_sleep',
            },
            {
              label: '6-8 hours',
              value: '6-8 hours',
              answerSlug: 'optimal_sleep',
            },
            {
              label: 'More than 8 hours',
              value: 'More than 8 hours',
              answerSlug: 'excessive_sleep',
            },
          ],
        },
        {
          id: 'q45',
          type: 'single-select',
          question: 'How would you describe the quality of your sleep?',
          questionSlug: 'sleep_quality',
          options: [
            {
              label: 'Restorative or refreshing',
              value: 'Restorative or refreshing',
              answerSlug: 'good_sleep_quality',
            },
            {
              label: 'Interrupted or disturbed',
              value: 'Interrupted or disturbed',
              answerSlug: 'poor_sleep_quality',
            },
            {
              label: 'Difficulty falling asleep or staying asleep',
              value: 'Difficulty falling asleep or staying asleep',
              answerSlug: 'insomnia',
            },
          ],
        },
        {
          id: 'q46',
          type: 'multi-select',
          question:
            'Do you experience any of the following symptoms related to your sleep?',
          questionSlug: 'sleep_symptoms',
          options: [
            {
              label: 'Snoring',
              value: 'Snoring',
              answerSlug: 'snoring',
            },
            {
              label: 'Waking up frequently at night',
              value: 'Waking up frequently at night',
              answerSlug: 'sleep_disruption',
            },
            {
              label: 'Frequent bathroom visits at night',
              value: 'Frequent bathroom visits at night',
              answerSlug: 'nocturia',
            },
            {
              label: 'Waking up gasping for air',
              value: 'Waking up gasping for air',
              answerSlug: 'sleep_apnea',
            },
            {
              label: 'Feeling unrefreshed upon waking',
              value: 'Feeling unrefreshed upon waking',
              answerSlug: 'unrefreshing_sleep',
            },
            {
              label: 'Falling asleep during the day',
              value: 'Falling asleep during the day',
              description: 'e.g., excessive daytime sleepiness',
              answerSlug: 'daytime_sleepiness',
            },
            {
              label: 'No',
              value: 'No',
            },
          ],
        },
      ],
    },
    {
      id: 'stress_management',
      title: 'Stress Management',
      questions: [
        {
          id: 'q47',
          type: 'slider',
          question:
            'How would you describe your stress levels on a scale from 1 to 10?',
          min: 0,
          max: 10,
          minLabel: 'No stress',
          maxLabel: 'Extreme stress',
          helperText: '1 being "No stress" and 10 being "Extreme stress."',
        },
        {
          id: 'q48',
          type: 'multi-select',
          question: 'What do you use to cope with stress?',
          questionSlug: 'stress_coping',
          options: [
            {
              label: 'Healthcare professional support',
              value: 'Healthcare professional support',
              answerSlug: 'professional_support',
            },
            {
              label: 'Friends or family',
              value: 'Friends or family',
              answerSlug: 'social_support',
            },
            {
              label: 'Physical activity or exercise',
              value: 'Physical activity or exercise',
              answerSlug: 'exercise_coping',
            },
            {
              label: 'Meditation or relaxation techniques',
              value: 'Meditation or relaxation techniques',
              answerSlug: 'meditation',
            },
            {
              label: 'Hobbies or personal interests',
              value: 'Hobbies or personal interests',
              answerSlug: 'hobbies',
            },
            {
              label: 'Spending time alone or having personal time',
              value: 'Spending time alone or having personal time',
              answerSlug: 'solitude',
            },
            {
              label: "I don't have any techniques",
              value: "I don't have any techniques",
              answerSlug: 'no_coping',
            },
          ],
        },
      ],
    },
    {
      id: 'tobacco_alcohol',
      title: 'Tobacco & Alcohol',
      questions: [
        {
          id: 'q49',
          type: 'single-select',
          question: 'Do you currently smoke?',
          helperText: 'Tobacco (cigarettes); not including vaping or sheesha',
          questionSlug: 'current_smoking',
          options: [
            { label: 'Yes', value: 'Yes', answerSlug: 'smokes_now' },
            {
              label: 'No, but I used to',
              value: 'No, but I used to',
              answerSlug: 'used_to_smoke',
            },
            {
              label: "No, I've never smoked",
              value: "No, I've never smoked",
              answerSlug: 'never_smoked',
            },
          ],
        },
        {
          id: 'q50',
          type: 'single-select',
          question: 'How much do you smoke?',
          options: [
            {
              label: 'Occasionally (smoke less than once a week)',
              value: 'Occasionally (smoke less than once a week)',
            },
            {
              label: '1-5 cigarettes per day',
              value: '1-5 cigarettes per day',
            },
            {
              label: '6-10 cigarettes per day',
              value: '6-10 cigarettes per day',
            },
            {
              label: '11-15 cigarettes per day',
              value: '11-15 cigarettes per day',
            },
            {
              label: '16-20 cigarettes per day (1 pack)',
              value: '16-20 cigarettes per day (1 pack)',
            },
            {
              label: '21-30 cigarettes per day (1 to 1.5 packs)',
              value: '21-30 cigarettes per day (1 to 1.5 packs)',
            },
            {
              label: '31+ cigarettes per day (2 packs or more)',
              value: '31+ cigarettes per day (2 packs or more)',
            },
            { label: 'Other', value: 'Other', additionalInfo: true },
          ],
          showIf: { q49: ['Yes'] },
        },
        {
          id: 'q51',
          type: 'single-select',
          question: 'How many years have you smoked?',
          options: [
            { label: 'Less than 1 year', value: 'Less than 1 year' },
            { label: '1-2 years', value: '1-2 years' },
            { label: '3-5 years', value: '3-5 years' },
            { label: '6-10 years', value: '6-10 years' },
            { label: '11-15 years', value: '11-15 years' },
            { label: '16-20 years', value: '16-20 years' },
            { label: 'More than 20 years', value: 'More than 20 years' },
          ],
          showIf: { q49: ['Yes'] },
        },
        {
          id: 'q52',
          type: 'single-select',
          question: 'How long did you smoke for?',
          options: [
            { label: 'Less than 1 year', value: 'Less than 1 year' },
            { label: '1-2 years', value: '1-2 years' },
            { label: '3-5 years', value: '3-5 years' },
            { label: '6-10 years', value: '6-10 years' },
            { label: '11-15 years', value: '11-15 years' },
            { label: '16-20 years', value: '16-20 years' },
            { label: 'More than 20 years', value: 'More than 20 years' },
          ],
          showIf: { q49: ['No, but I used to'] },
        },
        {
          id: 'q53',
          type: 'date',
          question: 'When did you quit smoking?',
          label: 'Date',
          showIf: { q49: ['No, but I used to'] },
          questionSlug: 'smoking_quit_date',
          maxDate: new Date(),
        },
        {
          id: 'q54',
          type: 'single-select',
          question: 'Do you currently vape or use sheesha?',
          questionSlug: 'current_vaping',
          options: [
            { label: 'Yes', value: 'Yes', answerSlug: 'vapes_now' },
            {
              label: 'No, but I used to',
              value: 'No, but I used to',
              answerSlug: 'used_to_vape',
            },
            {
              label: "No, I've never vaped or used sheesha",
              value: "No, I've never vaped or used sheesha",
              answerSlug: 'never_vaped',
            },
          ],
        },
        {
          id: 'q55',
          type: 'single-select',
          question: 'How much do you vape or use sheesha?',
          options: [
            {
              label: 'Occasionally (smoke less than once a week)',
              value: 'Occasionally (smoke less than once a week)',
            },
            { label: '1-5 times per day', value: '1-5 times per day' },
            { label: '6-10 times per day', value: '6-10 times per day' },
            { label: '11-15 times per day', value: '11-15 times per day' },
            { label: '16-20 times per day', value: '16-20 times per day' },
            { label: '21-30 times per day', value: '21-30 times per day' },
            { label: '31+ times per day', value: '31+ times per day' },
            { label: 'Other', value: 'Other', additionalInfo: true },
          ],
          showIf: { q54: ['Yes'] },
        },
        {
          id: 'q56',
          type: 'single-select',
          question: 'How many years have you vaped or used sheesha?',
          options: [
            { label: 'Less than 1 year', value: 'Less than 1 year' },
            { label: '1-2 years', value: '1-2 years' },
            { label: '3-5 years', value: '3-5 years' },
            { label: '6-10 years', value: '6-10 years' },
            { label: '11-15 years', value: '11-15 years' },
            { label: '16-20 years', value: '16-20 years' },
            { label: 'More than 20 years', value: 'More than 20 years' },
          ],
          showIf: { q54: ['Yes'] },
        },
        {
          id: 'q57',
          type: 'single-select',
          question: 'How long did you vape or use sheesha for?',
          options: [
            { label: 'Less than 1 year', value: 'Less than 1 year' },
            { label: '1-2 years', value: '1-2 years' },
            { label: '3-5 years', value: '3-5 years' },
            { label: '6-10 years', value: '6-10 years' },
            { label: '11-15 years', value: '11-15 years' },
            { label: '16-20 years', value: '16-20 years' },
            { label: 'More than 20 years', value: 'More than 20 years' },
          ],
          showIf: { q54: ['No, but I used to'] },
        },
        {
          id: 'q58',
          type: 'date',
          question: 'When did you quit vaping or using sheesha?',
          label: 'Date',
          showIf: { q54: ['No, but I used to'] },
          questionSlug: 'vaping_quit_date',
          maxDate: new Date(),
        },
        {
          id: 'q59',
          type: 'single-select',
          question: 'Do you currently drink alcohol?',
          questionSlug: 'current_alcohol',
          options: [
            { label: 'Yes', value: 'Yes', answerSlug: 'drinks_now' },
            {
              label: 'No, but I used to',
              value: 'No, but I used to',
              answerSlug: 'used_to_drink',
            },
            {
              label: "No, I've never consumed alcohol",
              value: "No, I've never consumed alcohol",
              answerSlug: 'never_drank',
            },
          ],
        },
        {
          id: 'q60',
          type: 'single-select',
          question: 'If you drink alcohol, how much do you normally drink?',
          helperText: 'Glasses per week',
          options: [
            { label: '1-3 glasses', value: '1-3 glasses' },
            { label: '4-7 glasses', value: '4-7 glasses' },
            { label: '8-14 glasses', value: '8-14 glasses' },
            { label: '15-20 glasses', value: '15-20 glasses' },
            { label: '21+ glasses', value: '21+ glasses' },
          ],
          showIf: { q59: ['Yes'] },
        },
      ],
    },
  ],
};

export default healthQuestionnaire;
